import { Injectable, inject } from '@angular/core';
import { computed, signal } from '@angular/core';
import { AuthenticationService } from '../../authentication/authentication.service';
import { NotificationService as MessageService } from './notification.service';
import { INotificationReceiver } from './notification.model';
import { lastValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { groupBy, uniqBy } from 'lodash-es';
import { UtilityService } from '../../services/utility.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  protected readonly _notifications = signal<INotificationReceiver[]>([]);
  readonly loadedNotifications = signal(false);
  readonly readMap = signal<{ [id: number]: true }>({});
  readonly notifications = computed(() => {
    const readMap = this.readMap();
    return uniqBy(this._notifications(), (i) => i.id).map((x) => ({
      ...x,
      read: x.read || readMap[x.id!],
      _dateSent: (x.createdOn || '').slice(0, 10),
    }));
  });
  readonly notificationGroups = computed(() => {
    return Object.entries(groupBy(this.notifications(), (i) => i._dateSent)).map(([date, listByDate]) => ({
      date,
      listByDate,
      titleGroup: Object.entries(groupBy(listByDate, (i) => i._notification?.title || 'Notification')).map(
        ([_, listByTitle]) => ({
          title: listByTitle[0]._notification?.title || 'Notification',
          listByTitle,
        }),
      ),
    }));
  });
  readonly loading = signal(true);
  readonly showOnlyUnread = signal(true);
  // readonly showOnlyUnread = signal(false);
  readonly total = signal(0);
  readonly canLoadMore = computed<boolean>(() => this.notifications()?.length < this.total());

  protected readonly pageSize = signal(10);
  protected readonly pageNumber = signal(0);
  protected readonly authS = inject(AuthenticationService);

  constructor(
    public mS: MessageService,
    public uS: UtilityService,
  ) {
    // this.mS.newNotification$.subscribe((r) => {
    //   this._notifications.update((list) => [r, ...list]);
    // });
  }

  async loadMore() {
    this.loading.set(true);
    this.loadedNotifications.set(true);
    this.pageNumber.update((num) => num + 1);
    try {
      const res = await lastValueFrom(
        this.mS.searchNotificationReceivers({
          pageSize: this.pageSize(),
          assignedTo: this.authS.authObj()?.userDetails?.username,
          pageNumber: this.pageNumber(),
          sortBy: 'createdOn',
          sortDirection: 'DESC',
          read: this.showOnlyUnread() ? false : undefined,
        }),
      );
      const list = res.content ?? [];
      const total = res.totalElements ?? 0;
      this._notifications.update((arr) => arr.concat(list));
      this.total.set(total);
    } catch (error) {
      throw error;
    }
    this.loading.set(false);
  }

  markAsUnread(notification: INotificationReceiver) {
    if (notification.read) return;
    this.readMap.update((mp) => {
      return { ...mp, [notification.id!]: true };
    });
    lastValueFrom(this.mS.markAsRead(notification.id!, notification));
  }

  toggleShowOnlyUnread(showOnlyUnread: boolean) {
    this.showOnlyUnread.set(showOnlyUnread);
    this.pageNumber.set(0);
    this._notifications.set([]);
    this.loadMore();
  }
}
